import { atom } from "jotai";
import { atomsWithMutation } from "jotai-tanstack-query";

import { EnrolmentDataInterface } from "@ts/interfaces/Enrol/EnrolmentDataInterface";
import { ParentEnrolInterface } from "@ts/interfaces/Enrol/ParentEntrolInterface";
import { StudentEnrolInterface } from "@ts/interfaces/Enrol/StudentEntrolInterface";
import { TokenInfoInterface } from "@ts/interfaces/Enrol/TokenInfoInterface";
import { CreateParentEnrolmentApiInterface } from "@ts/interfaces/Enrol/api/CreateParentEnrolmentApiInterface";
import { CreateStudentEnrolmentApiInterface } from "@ts/interfaces/Enrol/api/CreateStudentEnrolmentApiInterface.ts";

import {
    enrolParentApi,
    enrolStudentApi,
    updateSubscriptionGrade
} from "@api/enrolApi";

export const enrolmentDataAtom = atom<EnrolmentDataInterface>({
    // By default, we should try to enrol both Parent & Student
    pendingStudentEnrolment: true,
    parentEmail: "",
    studentOnly: false,
    token: ""
});

export const tokenInfoAtom = atom<TokenInfoInterface>({
    programs: null,
    student_id: 0,
    student_first_name: "",
    student_last_name: ""
});

export const enrolParentAtom = atom<ParentEnrolInterface>({
    firstName: "",
    lastName: "",
    reasonId: null,
    referralSourceId: null,
    password: ""
});

export const enrolStudentAtom = atom<StudentEnrolInterface>({
    firstName: "",
    lastName: "",
    startingTerm: "",
    program: null,
    subjectPerformances: [
        {
            id: 1,
            subjectName: "Mathematics",
            level: 1,
            isEnabled: true,
            subFields: null
        },
        {
            id: 2,
            subjectName: "English",
            level: 1,
            isEnabled: true,
            subFields: null
        },
        {
            id: 3,
            subjectName: "The arts",
            level: 1,
            isEnabled: true,
            subFields: [
                {
                    id: 1,
                    subFieldName: "Drama"
                },
                {
                    id: 2,
                    subFieldName: "Music"
                },
                {
                    id: 3,
                    subFieldName: "Visual arts"
                }
            ]
        },
        {
            id: 4,
            subjectName: "Humanities",
            level: 1,
            isEnabled: true,
            subFields: [
                {
                    id: 1,
                    subFieldName: "Geography"
                },
                {
                    id: 2,
                    subFieldName: "History"
                },
                {
                    id: 3,
                    subFieldName: "Civics"
                },
                {
                    id: 4,
                    subFieldName: "Economics"
                }
            ]
        },
        {
            id: 5,
            subjectName: "PDHPE",
            level: 1,
            isEnabled: true,
            subFields: [
                {
                    id: 1,
                    subFieldName: "Health"
                },
                {
                    id: 2,
                    subFieldName: "Physical education"
                }
            ]
        }
    ]
});

export const [, createParentEnrolmentAtom] = atomsWithMutation(() => ({
    mutationKey: ["createParentEnrolment"],
    mutationFn: async (data: CreateParentEnrolmentApiInterface) => {
        return (await enrolParentApi(data)).data;
    }
}));

export const [, createStudentEnrolmentAtom] = atomsWithMutation(() => ({
    mutationKey: ["createStudentEnrolment"],
    mutationFn: async (data: CreateStudentEnrolmentApiInterface) => {
        return (await enrolStudentApi(data)).data;
    }
}));

export const [, tokenInfoUpdateAtom] = atomsWithMutation(() => ({
    mutationKey: ["updateTokenInfo"],
    mutationFn: async (data: TokenInfoInterface) => {
        if (!data.programs) {
            return;
        }
        const tokenInfo = {
            gradeId: data.programs?.gradeID,
            id: data.programs?.id
        };
        return (await updateSubscriptionGrade(tokenInfo)).data;
    }
}));

export const premiumServiceListAtom = atom<[]>([]);

enrolmentDataAtom.debugLabel = "enrolmentDataAtom";
tokenInfoAtom.debugLabel = "tokenInfoAtom";
enrolParentAtom.debugLabel = "enrolParentAtom";
enrolStudentAtom.debugLabel = "enrolStudentAtom";
createParentEnrolmentAtom.debugLabel = "createParentEnrolmentAtom";
createStudentEnrolmentAtom.debugLabel = "createStudentEnrolmentAtom";
premiumServiceListAtom.debugLabel = "premiumServiceListAtom";
